import { useTranslation } from 'react-i18next';
// frontend/src/components/chat/VoiceRecorder.jsx
// Grabadora premium de notas de voz con MediaRecorder (APIs nativas, sin librerías).
// Flujo: mic → grabar (con onda EN VIVO, pausar/continuar) → detener →
//        previsualizar (reproducir/regrabar) → enviar/cancelar.
// Al enviar calcula un waveform (picos 0..1) desde el audio grabado y lo manda
// junto al archivo con attachment_kind='voice', is_voice=true, duration_seconds.
import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography, CircularProgress } from '@mui/material';
import {
  Mic as MicIcon, Delete as DeleteIcon,
  Send as SendIcon, FiberManualRecord as RecDotIcon,
  Pause as PauseIcon, PlayArrow as PlayIcon, Replay as ReplayIcon,
} from '@mui/icons-material';
import {
  VOICE_MAX_SECONDS, VOICE_MIME_CANDIDATES, pickSupportedMime, computeWaveformFromBlob,
} from './chatMediaConstants';

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

export const recordingSupported = () =>
  typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof window.MediaRecorder !== 'undefined';

// Barras de onda EN VIVO durante la grabación, alimentadas por AnalyserNode.
const LiveWave = ({ levels, paused }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: 24, mx: 0.5, minWidth: 90 }}>
    {levels.map((v, i) => (
      <Box key={i} sx={{
        width: 3, borderRadius: 2,
        height: `${Math.max(8, v * 100)}%`,
        bgcolor: paused ? 'grey.500' : 'error.main',
        transition: 'height 90ms linear',
      }} />
    ))}
  </Box>
);

const VoiceRecorder = ({ onSend, onStateChange, disabled }) => {
  const { t } = useTranslation();
  const [state, setState] = useState('idle'); // idle | recording | paused | preview
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState(null);
  const [levels, setLevels] = useState(Array(18).fill(0.1));
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [sending, setSending] = useState(false);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const blobRef = useRef(null);
  const durationRef = useRef(0);
  const previewAudioRef = useRef(null);
  // Audio analysis (onda en vivo)
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => () => cleanup(), []); // eslint-disable-line
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const stopAnalyser = () => {
    cancelAnimationFrame(rafRef.current);
    try { audioCtxRef.current?.close(); } catch { /* noop */ }
    audioCtxRef.current = null;
    analyserRef.current = null;
  };

  const cleanup = () => {
    clearInterval(timerRef.current);
    stopAnalyser();
    try { if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop(); } catch { /* noop */ }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
    try { previewAudioRef.current?.pause(); } catch { /* noop */ }
  };

  const startAnalyser = (stream) => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        if (!analyserRef.current) return;
        analyser.getByteFrequencyData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i += 1) sum += data[i];
        const level = Math.min(1, (sum / data.length) / 90);
        setLevels((prev) => [...prev.slice(1), level]);
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch { /* onda en vivo es opcional */ }
  };

  const start = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickSupportedMime(VOICE_MIME_CANDIDATES);
      const rec = new window.MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      blobRef.current = null;
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const type = rec.mimeType || mime || 'audio/webm';
        blobRef.current = new Blob(chunksRef.current, { type });
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        stopAnalyser();
      };
      rec.start(250);
      recorderRef.current = rec;
      setSeconds(0);
      durationRef.current = 0;
      setLevels(Array(18).fill(0.1));
      setState('recording');
      onStateChange?.(true);
      startAnalyser(stream);
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          const n = s + 1;
          durationRef.current = n;
          if (n >= VOICE_MAX_SECONDS) stop();
          return n;
        });
      }, 1000);
    } catch (e) {
      const denied = e?.name === 'NotAllowedError' || e?.name === 'SecurityError';
      setError(denied
        ? t('hardcoded.components_chat_voicerecorder.permiso_de_microfono_denegado_activalo')
        : t('hardcoded.components_chat_voicerecorder.no_se_pudo_acceder_al'));
      onStateChange?.(false);
    }
  };

  const pause = () => {
    try { recorderRef.current?.pause(); } catch { /* noop */ }
    clearInterval(timerRef.current);
    cancelAnimationFrame(rafRef.current);
    setState('paused');
  };

  const resume = () => {
    try { recorderRef.current?.resume(); } catch { /* noop */ }
    if (streamRef.current && !audioCtxRef.current) startAnalyser(streamRef.current);
    setState('recording');
    timerRef.current = setInterval(() => {
      setSeconds((s) => {
        const n = s + 1;
        durationRef.current = n;
        if (n >= VOICE_MAX_SECONDS) stop();
        return n;
      });
    }, 1000);
  };

  const stop = () => {
    clearInterval(timerRef.current);
    cancelAnimationFrame(rafRef.current);
    try { recorderRef.current?.stop(); } catch { /* noop */ }
    setState('preview');
    // Construir URL de previsualización cuando el blob esté listo.
    (async () => {
      for (let i = 0; i < 40 && !blobRef.current; i += 1) {
        await new Promise((r) => setTimeout(r, 25)); // eslint-disable-line no-await-in-loop
      }
      if (blobRef.current) setPreviewUrl(URL.createObjectURL(blobRef.current));
    })();
  };

  const togglePreview = async () => {
    const el = previewAudioRef.current;
    if (!el) return;
    if (previewPlaying) { el.pause(); return; }
    try { await el.play(); } catch { /* noop */ }
  };

  const cancel = () => {
    cleanup();
    blobRef.current = null;
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    setPreviewPlaying(false);
    setState('idle');
    setSeconds(0);
    setError(null);
    onStateChange?.(false);
  };

  const reRecord = () => {
    cleanup();
    blobRef.current = null;
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    setPreviewPlaying(false);
    setState('idle');
    setSeconds(0);
    // arranca de inmediato una nueva grabación
    setTimeout(() => start(), 0);
  };

  const send = async () => {
    if (sending) return;
    for (let i = 0; i < 40 && !blobRef.current; i += 1) {
      await new Promise((r) => setTimeout(r, 25)); // eslint-disable-line no-await-in-loop
    }
    const blob = blobRef.current;
    if (!blob || !blob.size) { cancel(); return; }
    setSending(true);
    const waveform = await computeWaveformFromBlob(blob);
    const ext = blob.type.includes('mp4') ? 'm4a' : blob.type.includes('ogg') ? 'ogg' : 'webm';
    const file = new File([blob], `nota-de-voz-${Date.now()}.${ext}`, { type: blob.type });
    onSend?.(file, durationRef.current, { waveform });
    setSending(false);
    cancel();
  };

  // Enviar mientras se graba: detiene la grabación y la envía en un solo gesto
  // (no hay botón "detener" separado).
  const stopAndSend = () => {
    if (sending) return;
    clearInterval(timerRef.current);
    cancelAnimationFrame(rafRef.current);
    try { recorderRef.current?.stop(); } catch { /* noop */ }
    send(); // espera el blob (onstop) y envía
  };

  if (!recordingSupported()) return null;

  if (state === 'idle') {
    return (
      <Tooltip title={error || t('hardcoded.components_chat_voicerecorder.enviar_nota_de_voz')}>
        <span>
          <IconButton onClick={start} disabled={disabled} color={error ? 'error' : 'primary'}><MicIcon /></IconButton>
        </span>
      </Tooltip>
    );
  }

  const recording = state === 'recording';
  const paused = state === 'paused';
  const preview = state === 'preview';

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{
      px: 1, py: 0.25, borderRadius: 2, flexWrap: 'wrap',
      bgcolor: recording ? 'error.50' : preview ? 'success.50' : 'grey.100',
      border: '1px solid', borderColor: recording ? 'error.main' : preview ? 'success.main' : 'divider',
    }}>
      {(recording || paused) && (
        <>
          <RecDotIcon sx={{
            color: paused ? 'grey.500' : 'error.main', fontSize: 14,
            animation: paused ? 'none' : 'pulse 1.2s infinite',
            '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.25 } },
          }} />
          <Typography variant="caption" sx={{ fontWeight: 700, color: paused ? 'text.secondary' : 'error.main', minWidth: 38 }}>
            {fmt(seconds)}
          </Typography>
          <LiveWave levels={levels} paused={paused} />
          {recording
            ? <Tooltip title={t('hardcoded.components_chat_voicerecorder.pausar')}><IconButton size="small" onClick={pause}><PauseIcon fontSize="small" /></IconButton></Tooltip>
            : <Tooltip title={t('hardcoded.components_auth_joincompany.continuar')}><IconButton size="small" color="error" onClick={resume}><PlayIcon fontSize="small" /></IconButton></Tooltip>}
          <Tooltip title={t('hardcoded.components_chat_voicerecorder.enviar_nota_de_voz')}>
            <span>
              <IconButton size="small" color="primary" onClick={stopAndSend} disabled={sending}>
                {sending ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </>
      )}

      {preview && (
        <>
          <audio
            ref={previewAudioRef}
            src={previewUrl || undefined}
            onPlay={() => setPreviewPlaying(true)}
            onPause={() => setPreviewPlaying(false)}
            onEnded={() => setPreviewPlaying(false)}
          />
          <Tooltip title={previewPlaying ? t('hardcoded.components_chat_voicerecorder.pausar') : t('hardcoded.components_chat_voicerecorder.escuchar')}>
            <span>
              <IconButton size="small" color="primary" onClick={togglePreview} disabled={!previewUrl}>
                {previewPlaying ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 38 }}>{fmt(seconds)}</Typography>
          <Typography variant="caption" color="text.secondary">{t('hardcoded.components_chat_voicerecorder.lista_para_enviar')}</Typography>
          <Tooltip title={t('hardcoded.components_chat_videonoterecorder.regrabar')}><IconButton size="small" onClick={reRecord}><ReplayIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title={t('hardcoded.components_chat_voicerecorder.enviar_nota_de_voz')}>
            <span>
              <IconButton size="small" color="primary" onClick={send} disabled={sending}>
                {sending ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </>
      )}

      <Tooltip title={t('common.cancel')}>
        <IconButton size="small" onClick={cancel}><DeleteIcon fontSize="small" /></IconButton>
      </Tooltip>
      {error && <Typography variant="caption" color="error" sx={{ width: '100%' }}>{error}</Typography>}
    </Stack>
  );
};

export default VoiceRecorder;
